import { memo, useContext } from 'react';
import { Link } from 'react-router-dom';
import { ContentContext } from '../contexts/ContentContext';
import '../styles/footer.css';

const Footer = memo(function Footer() {
  const { content } = useContext(ContentContext);
  const f    = content.footer  || {};
  const year = new Date().getFullYear();

  const phone     = f.contactPhone   || '226-220-8759';
  const email     = f.contactEmail   || 'klaivoxai@gmail.com';
  const website   = f.contactWebsite || 'klaivox.com';
  const hours     = f.hours          || 'Monday – Friday, 9:00 AM – 6:00 PM EST';
  const tagline   = f.tagline        || 'Intelligent Conversations. Everywhere.\nDeploy fully branded AI agents under your name.';
  const copyright = f.copyright      || 'Klaivox. All rights reserved.';
  const igUrl     = f.instagramUrl   || 'https://www.instagram.com/klaivox_';

  return (
    <footer className="footer">
      <div className="footer__grid">

        {/* Column 1 — Brand */}
        <div>
          <Link to="/" className="footer__brand-logo" aria-label="Klaivox home">
            <img src="/klaivox_icon_zoomed_2x.svg" alt="" className="footer__brand-icon" aria-hidden="true" />
            <span><span className="logo-k">K</span>laivox</span>
          </Link>
          <p className="footer__tagline">
            {tagline.split('\n').map((line, i) => (
              <span key={i}>{line}{i < tagline.split('\n').length - 1 && <br />}</span>
            ))}
          </p>
          <div className="footer__socials">
            {/* LinkedIn */}
            <a href="https://linkedin.com" className="footer__social-icon" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
                <rect x="2" y="9" width="4" height="12"/>
                <circle cx="4" cy="4" r="2"/>
              </svg>
            </a>
            {/* Twitter/X */}
            <a href="https://twitter.com" className="footer__social-icon" aria-label="Twitter / X" target="_blank" rel="noopener noreferrer">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4l16 16M4 20L20 4"/>
              </svg>
            </a>
            {/* Facebook */}
            <a href="https://facebook.com" className="footer__social-icon" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
              </svg>
            </a>
            {/* Instagram */}
            <a href={igUrl} className="footer__social-icon" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                <circle cx="12" cy="12" r="4"/>
                <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
              </svg>
            </a>
          </div>
        </div>

        {/* Column 2 — Pages */}
        <div>
          <p className="footer__col-title">Pages</p>
          <ul className="footer__nav-links">
            <li><Link to="/">Home</Link></li>
            <li><Link to="/features">Platform</Link></li>
            <li><Link to="/pricing">Pricing</Link></li>
            <li><Link to="/onboarding">Onboarding</Link></li>
            <li><Link to="/contact">Contact</Link></li>
          </ul>
        </div>

        {/* Column 3 — Contact */}
        <div>
          <p className="footer__col-title">Contact</p>
          <div className="footer__contact-item">
            <span className="footer__contact-icon">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 16.92z"/>
              </svg>
            </span>
            <span>{phone}</span>
          </div>
          <div className="footer__contact-item">
            <span className="footer__contact-icon">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
            </span>
            <a href={`mailto:${email}`}>{email}</a>
          </div>
          <div className="footer__contact-item">
            <span className="footer__contact-icon">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
            </span>
            <a href={`https://${website}`} target="_blank" rel="noopener noreferrer">{website}</a>
          </div>
          <div className="footer__contact-item">
            <span className="footer__contact-icon">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12,6 12,12 16,14"/>
              </svg>
            </span>
            <span>{hours}</span>
          </div>
        </div>

        {/* Column 4 — Trust */}
        <div>
          <p className="footer__col-title">Why Klaivox</p>
          <div className="footer__trust-badges">
            <div className="footer__trust-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              <span>No monthly fees ever</span>
            </div>
            <div className="footer__trust-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
              <span>100% white-labeled</span>
            </div>
            <div className="footer__trust-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
              </svg>
              <span>Live in under 24 hours</span>
            </div>
            <div className="footer__trust-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="1" x2="12" y2="23"/>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
              <span>Keep 100% of revenue</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="footer__bottom">
        <p className="footer__copy">© {year} {copyright}</p>
        <div className="footer__bottom-links">
          <a href="#privacy">Privacy Policy</a>
          <a href="#terms">Terms of Service</a>
        </div>
      </div>
    </footer>
  );
});

export default Footer;
